<template>
	<component
		:is="componentLayout"
		v-model="showOptionsList"
		:disabled="disabled"
		:label="label"
		:isListScrolledToTop="isListScrolledToTop"
		class="ao-great-select"
	>
		<template #activator="{ activatorProps }">

			<SelectActivator
				v-bind="{
					...activatorProps,
					...selectActivatorProps
				}"
				@click="isMobile && toggleOptionsList()"
				@click:clear="onClickClear"
				@click:chipClose="handleItemSelect"
			/>

		</template>

		<template #errors>

			<div
				:class="{
					'v-input--error': error,
					'v-input--disabled': disabled,
				}"
			>
				<div
					v-if="hideDetails === false || (hideDetails === 'auto' && errorMessages.length)"
					class="v-input__details"
				>
					<div class="v-messages">
						<div
							v-for="(errorMessage, idx) in errorMessages"
							:key="idx"
							class="v-messages__message"
						>
							<span>
								{{ errorMessage }}
							</span>
						</div>
					</div>
				</div>
			</div>

		</template>

		<template #addAction>
			<slot name="action">
				<AoIconBtn
					v-if="showAddButton"
					class="ml-3 mt-3"
					color="primary"
					iconName="plus"
					@click="$emit('onClickPlus')"
				/>
			</slot>
		</template>

		<template #default>
			<SelectList
				v-bind="selectListProps"
				@update:searchQuery="updateSearchQuery"
				@click:toggleSelectAll="toggleSelectAll"
				@click:toggleShowFullList="toggleShowFullList"
				@click:item="onClickItem"
				@update:scrolledToTop="isListScrolledToTop = $event"
			/>
		</template>
	</component>
</template>

<script setup>
import { ref, computed, watch } from 'vue'

import { useDeviceStore } from '@ao/store'

import MobileSelectLayout from './MobileSelectLayout.vue'
import DesktopSelectLayout from './DesktopSelectLayout.vue'
import SelectActivator from './SelectActivator.vue'
import SelectList from './SelectList.vue'

import AoIconBtn from '../AoIconBtn.vue'

import { useGreatSelect } from './useGreatSelect'

const props = defineProps({

	// ДАННЫЕ
	modelValue: {
		type: [Array, Number, String],
		default: null,
	},

	suggestedItems: {
		type: Array,
		default: () => [],
	},

	items: {
		type: Array,
		default: () => [],
	},

	itemsFull: {
		type: Array,
		default: () => [],
	},

	itemValue: {
		type: String,
		default: 'value',
	},

	itemTitle: {
		type: String,
		default: 'title',
	},

	itemSubtitle: {
		type: String,
		default: null,
	},

	itemText: {
		type: String,
		default: null,
	},

	multiselect: {
		type: Boolean,
		default: false,
	},

	// UX
	disabled: {
		type: Boolean,
		default: false,
	},

	clearable: {
		type: Boolean,
		default: false,
	},

	isSearch: {
		type: Boolean,
		default: false,
	},

	searchMinChars: {
		type: Number,
		default: 1,
	},

	/**
   * Показывать ли чекбокс "Выбрать всё" в режиме мультиселекта.
   */
  showSelectAll: {
		type: Boolean,
		default: false,
	},

	isSorted: {
		type: Boolean,
		default: true,
	},

	sortCompareCallback: {
		type: Function,
		default: null,
	},

	closableChips: {
		type: Boolean,
		default: true,
	},

	openOnMounted: {
		type: Boolean,
		default: false,
	},

	showAddButton: {
		type: Boolean,
		default: false,
	},

	// UI
	label: {
		type: String,
		default: null,
	},

	variant: {
		type: String,
		default: 'underlined',
	},

	color: {
		type: String,
		default: undefined,
	},

	density: {
		type: String,
		default: 'default',
	},

	// TODO: попробовать избавиться (используется в одном месте)
	excludePadding: {
		type: Boolean,
		default: false,
	},

	chips: {
		type: Boolean,
		default: true,
	},

	// FIXME: заменить на hint (вроде как только один раз используется в проекте)
	messages: {
		type: String,
		default: null,
	},

	hideDetails: {
		type: [Boolean, String],
	},

	loading: {
		type: [Boolean, String],
	},

	prependIcon: {
		type: String,
		default: null,
	},

	menuMaxWidth: {
		type: Number,
	},

	itemPrependAvatar: {
		type: String,
		default: null,
	},

	itemPrependIcon: {
		type: String,
		default: null,
	},

	itemAppendAvatar: {
		type: String,
		default: null,
	},

	itemAppendIcon: {
		type: String,
		default: null,
	},

	// ВАЛИДАЦИЯ
	error: {
		type: Boolean,
		default: false,
	},

	errorMessages: {
		type: Array,
		default: () => [],
	},

})

const emit = defineEmits({
	'update:modelValue': null,
	'onClickPlus': null,
	'list:opened': null,
	'list:closed': null,
})

const {
	itemsToDisplay,
  selectedItems,
	searchQuery,
	updateSearchQuery,
  handleItemSelect,
	isAllSelected,
	toggleSelectAll,
	onClickClear,
	showFullList,
	isShowFullListToggleAvailable,
	toggleShowFullList,
} = useGreatSelect(props, emit)

const deviceStore = useDeviceStore()

const isMobile = computed(() => {
	return deviceStore.mobile
})

const componentLayout = computed(() => {
	return isMobile.value ? MobileSelectLayout : DesktopSelectLayout
})

const showOptionsList = ref(false)

const openOptionsList = () => {
	showOptionsList.value = true
}

const closeOptionsList = () => {
	showOptionsList.value = false
}

const toggleOptionsList = () => {
	showOptionsList.value = !showOptionsList.value
}

watch(showOptionsList, (value) => {
	if (!value) updateSearchQuery('')
	emit(value ? 'list:opened' : 'list:closed')
})

const isListScrolledToTop = ref(true)

const selectActivatorProps = computed(() => {
	return {
		isActive: showOptionsList.value,
		modelValue: props.modelValue,
		items: Array.isArray(selectedItems.value) ? selectedItems.value : (selectedItems.value ? [selectedItems.value] : []),
		itemTitle: props.itemTitle,
		itemValue: props.itemValue,
		multiselect: props.multiselect,
		label: props.label,
		loading: props.loading,
		disabled: props.disabled,
		clearable: props.clearable,
		variant: props.variant,
		color: props.color,
		density: props.density,
		chips: props.chips,
		closableChips: props.closableChips,
		hint: props.messages,
		hideDetails: props.hideDetails,
		error: props.error,
		prependIcon: props.prependIcon,
	}
})

const selectListProps = computed(() => {
	return {
		items: itemsToDisplay.value,
		itemValue: props.itemValue,
		itemTitle: props.itemTitle,
		itemSubtitle: props.itemSubtitle,
		itemText: props.itemText,
		multiselect: props.multiselect,
		selectedItems: selectedItems.value,
		loading: props.loading,
		isSearch: props.isSearch,
		searchQuery: searchQuery.value,
		showSelectAll: props.showSelectAll,
		isAllSelected: isAllSelected.value,
		showFullList: showFullList.value,
		isShowFullListToggleAvailable: isShowFullListToggleAvailable.value,
		isMobile: isMobile.value,
		prependAvatar: props.itemPrependAvatar,
		prependIcon: props.itemPrependIcon,
		appendAvatar: props.itemAppendAvatar,
		appendIcon: props.itemAppendIcon,
	}
})

const onClickItem = (payload) => {
	handleItemSelect(payload)
	if (!props.multiselect) closeOptionsList()
}

defineExpose({
	openList: openOptionsList,
	closeList: closeOptionsList,
})

</script>
